import { useMutation, useQuery, useQueryClient } from 'react-query';
import { baseFetch } from '../api/baseFetch';
import { useToast } from '@raptormaps/toast';
import {
  PermissionsResponseSchema,
  PermissionsUpdateResponseSchema,
  type PermissionsResponse,
  type PermissionsUpdateResponse,
} from '../api/schemas/PermissionsResponseSchema';
import type { OrgUser } from '../api/types/OrgUser';
import type { UserPermissions } from '../api/schemas/UserPermissionsSchema';
import { RAPTOR_ADMIN_ROLE } from '../components/Roles/constants';

export const useGlobalUserRoles = (userIds: string[]) => {
  const toast = useToast();

  const getGlobalUserRoles = async () => {
    let users: UserPermissions[] = [];
    let fetchedAllUsers = false;
    let nextToken = '';

    while (!fetchedAllUsers) {
      await baseFetch(
        'auth',
        `/systems/global/user-permissions`,
        PermissionsResponseSchema,
        {
          headers: {
            'Cache-Control': 'max-age=0',
          },
          searchParams: {
            user_ids: userIds,
            pagination_token: nextToken,
          },
        },
        // eslint-disable-next-line no-loop-func
      ).then((res: PermissionsResponse) => {
        users = [...users, ...res.user_permissions];

        if (res.meta.next_token === '') {
          fetchedAllUsers = true;
        } else {
          nextToken = res.meta.next_token;
        }
      });
    }

    return users;
  };

  return useQuery(['getGlobalUserRoles', userIds], getGlobalUserRoles, {
    enabled: !!userIds?.length,
    onError: () => {
      toast.error('Error fetching roles for organization.');
    },
  });
};

export const useInvalidateGlobalUserRoles = () => {
  const queryClient = useQueryClient();
  return {
    invalidateGlobalUserRoles: () =>
      queryClient.invalidateQueries(['getGlobalUserRoles']),
  };
};

export const useUpdateGlobalUserRoles = () => {
  const toast = useToast();
  const { invalidateGlobalUserRoles } = useInvalidateGlobalUserRoles();

  const updateGlobalUserRoles = async (users: OrgUser[]) => {
    const formattedUsers = users.map(user => {
      if (user.raptoradmin) {
        return {
          action: 'create',
          value: {
            user_type: 'user',
            user_id: user.auth0UserId,
            role_ids: [RAPTOR_ADMIN_ROLE],
          },
        };
      } else {
        return {
          action: 'delete',
          path: `/${user.auth0UserId}?role_ids=${RAPTOR_ADMIN_ROLE}`,
        };
      }
    });

    await baseFetch(
      'auth',
      `/systems/global/user-permissions/bulk_update`,
      PermissionsUpdateResponseSchema,
      {
        method: 'POST',
        requestBody: formattedUsers,
      },
    ).then((res: PermissionsUpdateResponse) => {
      const errors = res.actions.map(action => action.errors).flat();
      errors.forEach(error => {
        if (error.code >= 500) {
          toast.error(error.message);
        } else if (error.code >= 400) {
          toast.warning(error.message);
        }
      });

      if (!errors?.length) {
        toast.success('Roles updated successfully.');
      }
    });
  };

  return useMutation(['updateGlobalUserRoles'], updateGlobalUserRoles, {
    onSuccess: () => invalidateGlobalUserRoles(),
  });
};
